/**
 * HITL-1511: detect an audio websocket that is open but silent.
 *
 * During an active HITL session the store streams audio continuously
 * (~50 frames/sec, silence included), so a delivery gap of a few seconds
 * while the socket is OPEN means the backend stopped routing audio to this
 * client — the failure mode of the 2026-08-12 incident, which was invisible
 * in New Relic because only connect/close/error were logged.
 *
 * The watchdog is armed while (socket open && session active) and reports
 * once per continuous gap; a frame after a reported gap yields a single
 * recovery event carrying the gap duration. Timestamps are passed in by the
 * caller so the class stays clock-free and unit-testable.
 */

export const AUDIO_SILENCE_THRESHOLD_MS = 10_000;

export type AudioSilenceEvent =
  | { type: 'gap'; silentForMs: number }
  | { type: 'recovered'; gapMs: number };

export class AudioSilenceWatchdog {
  private armed = false;

  // Start of the current frameless stretch: arm time, or the last frame.
  private silenceSince = 0;

  private gapReported = false;

  constructor(
    private readonly thresholdMs: number = AUDIO_SILENCE_THRESHOLD_MS
  ) {}

  /**
   * Keep the watchdog in sync with its preconditions (socket open AND
   * session active). Disarming clears any open gap without reporting it —
   * close/reconnect paths have their own logging.
   */
  setArmed(armed: boolean, now: number): void {
    if (armed === this.armed) {
      return;
    }
    this.armed = armed;
    this.silenceSince = now;
    this.gapReported = false;
  }

  /**
   * Record an incoming audio frame. Returns a recovery event when the frame
   * ends a gap that was already reported, otherwise null.
   */
  noteFrame(now: number): AudioSilenceEvent | null {
    if (!this.armed) {
      return null;
    }
    const event: AudioSilenceEvent | null = this.gapReported
      ? { type: 'recovered', gapMs: now - this.silenceSince }
      : null;
    this.silenceSince = now;
    this.gapReported = false;
    return event;
  }

  /**
   * Periodic check. Returns a gap event the first time the frameless stretch
   * exceeds the threshold, then stays quiet until frames resume.
   */
  tick(now: number): AudioSilenceEvent | null {
    if (!this.armed || this.gapReported) {
      return null;
    }
    const silentForMs = now - this.silenceSince;
    if (silentForMs < this.thresholdMs) {
      return null;
    }
    this.gapReported = true;
    return { type: 'gap', silentForMs };
  }
}
